import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { CheckCircleBlue } from '@/components/Icons/CheckCircleBlue';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { ClearCircle } from '@/components/Icons/ClearCircle';
import { Close } from '@/components/Icons/Close';
import { Search } from '@/components/Icons/Search';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useRecipientGroupMembers } from '@/pages/Mail/hooks/useRecipientGroupMembers';
import { useRecipientGroups } from '@/pages/Mail/hooks/useRecipientGroups';
import { ButtonVariant, Size } from '@/types/components';
import type {
  Recipient,
  RecipientGroupMemberResponse,
  RecipientGroupResponse,
} from '@/types/mail';
import { getRecipientKey } from '@/utils/mail';
import { getFullName, removeDiacritics } from '@/utils/text';
import { tv } from '@/utils/tv';

const TITLE_FADE_RANGE_PX = 16;

const groupCard = tv({
  base: 'flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl px-4 py-[18px] text-left transition-colors',
  variants: {
    isActive: {
      true: 'bg-info-surface text-primary',
      false: 'bg-on-primary-surface text-muted-foreground hover:bg-hover',
    },
  },
});

interface Props {
  recipients: Recipient[];
  onClose: () => void;
  onRegisterBack?: (requestBack: () => void) => void;
  onSave: (recipients: Recipient[]) => void;
}

export const RecipientPickerView: React.FC<Props> = ({
  recipients,
  onClose,
  onRegisterBack,
  onSave,
}) => {
  const { t } = useTranslation();
  const {
    groups,
    studyYear,
    isFetching: isGroupsFetching,
  } = useRecipientGroups();

  const [openGroup, setOpenGroup] = useState<RecipientGroupResponse | null>(
    null,
  );
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(recipients);
  const groupListFade = useScrollFadeProgress(TITLE_FADE_RANGE_PX);
  const recipientListFade = useScrollFadeProgress(TITLE_FADE_RANGE_PX);

  const { members, isFetching: isMembersFetching } = useRecipientGroupMembers({
    groupId: openGroup?.id,
    studyYearId: studyYear?.id,
  });

  const yearLabel = studyYear
    ? t('profile.studyYearName', { name: studyYear.name })
    : null;
  const query = removeDiacritics(search.trim().toLowerCase());
  const visibleMembers = members.filter((member) =>
    removeDiacritics(
      `${member.lastName} ${member.firstName} ${member.description}`.toLowerCase(),
    ).includes(query),
  );
  const pickedKeys = new Set(picked.map(getRecipientKey));

  const selectedCount = (group: RecipientGroupResponse) =>
    picked.filter((recipient) => recipient.groupId === group.id).length;

  const saveLabel = (count: number) =>
    count > 0
      ? t('mail.composeModal.saveWithCount', { count })
      : t('mail.composeModal.save');

  const totalCount = picked.length;
  const openGroupCount = openGroup ? selectedCount(openGroup) : 0;

  const isPicked = (member: RecipientGroupMemberResponse) =>
    pickedKeys.has(`${openGroup?.id}-${member.id}`);

  const toggle = (member: RecipientGroupMemberResponse) => {
    if (!openGroup) {
      return;
    }

    if (isPicked(member)) {
      setPicked(
        picked.filter(
          (recipient) =>
            recipient.groupId !== openGroup.id || recipient.id !== member.id,
        ),
      );
      return;
    }

    setPicked([
      ...picked,
      {
        id: member.id,
        groupId: openGroup.id,
        name: getFullName(member),
        description: member.description,
      },
    ]);
  };

  const openMemberList = (group: RecipientGroupResponse) => {
    setOpenGroup(group);
    setSearch('');
  };

  const closeMemberList = () => {
    setOpenGroup(null);
    setSearch('');
  };

  const applyAndClose = () => onSave(picked);

  useEffect(() => {
    onRegisterBack?.(applyAndClose);
  });

  const renderMemberRow = (member: RecipientGroupMemberResponse) => (
    <button
      key={member.id}
      type="button"
      onClick={() => toggle(member)}
      className="flex cursor-pointer items-center gap-3 py-3 text-left"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="truncate text-subhead-14 text-foreground max-md:text-headline-20">
          {member.lastName} {member.firstName}
        </p>
        <p className="truncate text-body-13 text-subtle-foreground">
          {member.description}
        </p>
      </div>
      {isPicked(member) && (
        <CheckCircleBlue className="size-5 shrink-0 text-primary" />
      )}
      {!isPicked(member) && (
        <span className="size-5 shrink-0 rounded-full border border-subtle-foreground" />
      )}
    </button>
  );

  const hasMembers = !isMembersFetching && visibleMembers.length > 0;
  const isMemberListEmpty = !isMembersFetching && visibleMembers.length === 0;
  const memberRows = (
    <div className="flex flex-col">{visibleMembers.map(renderMemberRow)}</div>
  );
  const memberSpinner = (
    <div className="flex flex-1 items-center justify-center py-10">
      <PageSpinner className="size-[22px] text-subtle-foreground" />
    </div>
  );
  const emptyState = <EmptyState description={t('mail.searchEmpty')} />;

  const searchField = (
    <div className="flex items-center gap-2 rounded-xl bg-on-primary-surface px-3 py-2.5">
      <Search className="size-4 shrink-0 text-subtle-foreground" />
      <input
        value={search}
        placeholder={t('mail.composeModal.search')}
        onChange={(event) => setSearch(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-body-14 text-foreground outline-none placeholder:text-subtle-foreground"
      />
      {search && (
        <button
          type="button"
          aria-label={t('global.close')}
          onClick={() => setSearch('')}
          className="flex shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
        >
          <ClearCircle className="size-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-background md:animate-slide-in-right">
      <div className="hidden h-full flex-col md:flex">
        <div className="flex h-16 shrink-0 items-center justify-between px-5">
          <button
            type="button"
            onClick={applyAndClose}
            className="flex cursor-pointer items-center gap-1 text-foreground"
          >
            <ChevronLeft className="size-5 text-muted-foreground" />
            <span className="text-headline-20">
              {t('mail.composeModal.back')}
            </span>
          </button>
          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="flex size-6 cursor-pointer items-center justify-center text-foreground"
          >
            <Close />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          <AutoHideScrollArea className="flex w-[410px] shrink-0 flex-col border-r border-divider">
            <div className="flex flex-col gap-2 p-5">
              {yearLabel && (
                <p className="pb-1 text-callout-16 text-foreground">
                  {yearLabel}
                </p>
              )}
              {isGroupsFetching && (
                <div className="flex justify-center py-10">
                  <PageSpinner className="size-[22px] text-subtle-foreground" />
                </div>
              )}
              {groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => openMemberList(group)}
                  className={groupCard({
                    isActive: group.id === openGroup?.id,
                  })}
                >
                  <span className="text-subhead-14">{group.name}</span>
                  <div className="flex shrink-0 items-center gap-2">
                    {selectedCount(group) > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-card px-1.5 text-caption-13 font-medium text-foreground">
                        {selectedCount(group)}
                      </span>
                    )}
                    <ChevronRightFilled className="size-3.5" />
                  </div>
                </button>
              ))}
            </div>
          </AutoHideScrollArea>

          <div className="flex min-h-0 flex-1 flex-col">
            {openGroup && (
              <>
                <div className="flex shrink-0 flex-col gap-4 px-5 pt-5 pb-4">
                  <p className="text-subhead-14 text-muted-foreground">
                    {openGroup.name}
                  </p>
                  {searchField}
                </div>

                {isMembersFetching && memberSpinner}
                {isMemberListEmpty && emptyState}
                {hasMembers && (
                  <AutoHideScrollArea className="flex min-h-0 flex-1 flex-col">
                    <div className="px-5 pb-5">{memberRows}</div>
                  </AutoHideScrollArea>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end border-t border-divider px-5 py-4">
          <Button variant={ButtonVariant.Primary} onClick={applyAndClose}>
            {t('mail.composeModal.save')}
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-background md:hidden">
        {!openGroup && (
          <div className="flex min-h-0 flex-1 flex-col animate-zoom-in">
            <div className="relative flex shrink-0 items-center bg-background px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-2">
              <button
                type="button"
                onClick={applyAndClose}
                aria-label={t('mail.composeModal.back')}
                className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
              >
                <ArrowLeft />
              </button>
              <h2
                style={{ opacity: groupListFade.progress }}
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground"
              >
                {t('mail.composeModal.recipientListTitle')}
              </h2>
            </div>

            <div
              onScroll={groupListFade.handleScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4"
            >
              <h2 className="pt-2 pb-5 text-title-36 text-foreground">
                {t('mail.composeModal.recipientListTitle')}
              </h2>
              {yearLabel && (
                <p className="pb-3 text-callout-16 text-foreground">
                  {yearLabel}
                </p>
              )}
              {isGroupsFetching && (
                <div className="flex justify-center py-10">
                  <PageSpinner className="size-[22px] text-subtle-foreground" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => openMemberList(group)}
                    className="flex h-14 cursor-pointer items-center justify-between gap-2 rounded-xl bg-card px-4 text-left shadow-card"
                  >
                    <span className="text-subhead-14 text-foreground">
                      {group.name}
                    </span>
                    <div className="flex shrink-0 items-center gap-1 text-foreground">
                      {selectedCount(group) > 0 && (
                        <span className="flex h-6 min-w-6 items-center justify-center rounded-xl bg-info-surface px-2 text-subhead-14 text-foreground">
                          {selectedCount(group)}
                        </span>
                      )}
                      <ChevronRight className="size-4" strokeWidth="2" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {totalCount > 0 && (
              <div className="flex shrink-0 border-t border-divider px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.Large}
                  onClick={applyAndClose}
                  className="w-full"
                >
                  {saveLabel(totalCount)}
                </Button>
              </div>
            )}
          </div>
        )}

        {openGroup && (
          <div className="flex min-h-0 flex-1 flex-col animate-zoom-in">
            <div className="relative flex shrink-0 items-center bg-background px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-2">
              <button
                type="button"
                onClick={closeMemberList}
                aria-label={t('mail.composeModal.back')}
                className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
              >
                <ArrowLeft />
              </button>
              <h2
                style={{ opacity: recipientListFade.progress }}
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground"
              >
                {openGroup.name}
              </h2>
            </div>

            <div
              onScroll={recipientListFade.handleScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4"
            >
              <h2 className="pt-1 pb-4 text-title-36 text-foreground">
                {openGroup.name}
              </h2>
              <div className="sticky top-0 z-10 bg-background pb-4">
                {searchField}
              </div>
              {isMembersFetching && memberSpinner}
              {isMemberListEmpty && emptyState}
              {hasMembers && memberRows}
            </div>

            <div className="flex shrink-0 border-t border-divider px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
              <Button
                variant={ButtonVariant.Primary}
                size={Size.Large}
                disabled={openGroupCount === 0}
                onClick={applyAndClose}
                className="w-full"
              >
                {saveLabel(openGroupCount)}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
